import type { PlanEffect, PlanEffectEnvelope, PlanEmphasisMark, PlanPointerPlacement, PlanPresentation, PlanTextPanel } from '@toolproof-core/lib/_contract/scene-plan';

import type { PresentationEffect } from '@/_contract/episode';

function record(value: unknown, fields: readonly string[]): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid ScenePlan presentation object.');
    const result = value as Record<string, unknown>;
    for (const key of Object.keys(result)) {
        if (!fields.includes(key)) throw new Error(`Unknown ScenePlan presentation field '${key}'.`);
    }
    return result;
}

function text(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) throw new Error('ScenePlan presentation requires nonempty text.');
    return value;
}

export function parseTextPanel(value: unknown): PlanTextPanel {
    const raw = record(value, ['heading', 'groups', 'note']);
    const boundedText = (value: unknown, maximum: number) => {
        const result = text(value);
        if (result.length > maximum) throw new Error('Text panel text exceeds its length limit.');
        return result;
    };
    if (!Array.isArray(raw.groups) || raw.groups.length < 1 || raw.groups.length > 3) {
        throw new Error('Text panel requires one to three groups.');
    }
    return { heading: boundedText(raw.heading, 100), groups: raw.groups.map(value => {
        const group = record(value, ['heading', 'items']);
        if (!Array.isArray(group.items) || group.items.length < 1 || group.items.length > 6) {
            throw new Error('Text panel groups require one to six items.');
        }
        return { heading: boundedText(group.heading, 100), items: group.items.map(item => boundedText(item, 240)) };
    }), ...(raw.note === undefined ? {} : { note: boundedText(raw.note, 240) }) };
}

export function parseEmphasisMarks(value: unknown, caption?: string): readonly PlanEmphasisMark[] | undefined {
    if (value === undefined) return undefined;
    if (!Array.isArray(value) || !value.length
        || !value.every((mark: unknown): mark is PlanEmphasisMark =>
            mark === 'border' || mark === 'contour' || mark === 'pointer' || mark === 'spotlight')
        || new Set(value).size !== value.length) {
        throw new Error('Emphasis marks must be a nonempty, unique list of border, contour, pointer or spotlight.');
    }
    if (caption !== undefined && !value.includes('pointer')) throw new Error('Emphasis caption requires the pointer mark.');
    return value;
}

export function parsePresentationEffect<TTarget>(value: unknown,
    parseTarget: (value: unknown) => TTarget): PresentationEffect<TTarget> {
    const effect = record(value, ['effectKind', 'target', 'caption', 'marks', 'durationMs', 'angleRadians']);
    const target = parseTarget(effect.target);
    if (effect.effectKind === 'emphasis') {
        if (effect.durationMs !== undefined || effect.angleRadians !== undefined) throw new Error('Invalid emphasis fields.');
        const caption = effect.caption === undefined ? undefined : text(effect.caption);
        const marks = parseEmphasisMarks(effect.marks, caption);
        return { effectKind: 'emphasis', target,
            ...(caption === undefined ? {} : { caption }), ...(marks === undefined ? {} : { marks }) };
    }
    if (effect.effectKind !== 'orbit'
        || typeof effect.durationMs !== 'number' || !Number.isFinite(effect.durationMs) || effect.durationMs <= 0
        || typeof effect.angleRadians !== 'number' || !Number.isFinite(effect.angleRadians)
        || effect.angleRadians === 0 || Math.abs(effect.angleRadians) > Math.PI) {
        throw new Error('Invalid or conflicting orbit effect.');
    }
    if (effect.caption !== undefined || effect.marks !== undefined) throw new Error('Orbit effects take no caption or marks.');
    return { effectKind: 'orbit', target, durationMs: effect.durationMs, angleRadians: effect.angleRadians };
}

/** Shared by ScenePlan and concept segments; the hub may not reach the camera. */
export function parsePointerPlacement(value: unknown): PlanPointerPlacement {
    const raw = record(value, ['focus', 'depth', 'lift']);
    if ((raw.focus !== 'stage' && raw.focus !== 'targets')
        || typeof raw.depth !== 'number' || !(raw.depth >= 0 && raw.depth <= 0.9)
        || typeof raw.lift !== 'number' || !(raw.lift >= 0 && raw.lift <= 4)) throw new Error('Invalid pointer placement.');
    return { focus: raw.focus, depth: raw.depth, lift: raw.lift };
}

export function parsePlanPresentation<TEffect extends PlanEffectEnvelope = PlanEffect>(
    value: unknown, rawCues: unknown,
    parseEffects: (effects: readonly unknown[]) => readonly TEffect[] = effects => {
        if (effects.length) throw new Error('Trace presentation effects require an adapter parser.');
        return [];
    },
): readonly PlanPresentation<TEffect>[] {
    if (!Array.isArray(value) || !value.length) throw new Error('ScenePlan presentation requires segments.');
    const cueIds = Array.isArray(rawCues) ? rawCues.map(cue => cue?.id) : [];
    const ids = new Set<string>();
    let previous = -2;
    return value.map(raw => {
        const segment = record(raw, ['id', 'afterCue', 'until', 'cameraView', 'pointerPlacement', 'textPanel', 'effects']);
        const id = text(segment.id);
        const afterCue = segment.afterCue === undefined ? undefined : text(segment.afterCue);
        const index = afterCue === undefined ? -1 : cueIds.indexOf(afterCue);
        if (!/^[a-z][a-z0-9-]*$/.test(id) || ids.has(id) || (afterCue !== undefined && index < 0) || index <= previous) {
            throw new Error(`ScenePlan presentation '${id}' requires unique ids and ordered cue references.`);
        }
        ids.add(id);
        previous = index;
        const until = segment.until === undefined ? undefined : record(segment.until, ['landmark', 'phase']);
        if (until && until.phase !== undefined && until.phase !== 'start' && until.phase !== 'complete') {
            throw new Error('Invalid presentation end phase.');
        }
        const untilPhase = until?.phase === 'start' ? 'start' : until?.phase === 'complete' ? 'complete' : undefined;
        if (!Array.isArray(segment.effects)) throw new Error('Presentation requires effects.');
        const effects = parseEffects(segment.effects);
        return { id, effects, ...(afterCue === undefined ? {} : { afterCue }),
            ...(segment.textPanel === undefined ? {} : { textPanel: parseTextPanel(segment.textPanel) }),
            ...(segment.cameraView === undefined ? {} : { cameraView: text(segment.cameraView) }),
            ...(segment.pointerPlacement === undefined ? {} : { pointerPlacement: parsePointerPlacement(segment.pointerPlacement) }),
            ...(until === undefined ? {} : { until: { landmark: text(until.landmark),
                ...(untilPhase === undefined ? {} : { phase: untilPhase }) } }) };
    });
}
